import PrimeCheckbox from 'components/form/PrimeCheckbox';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { confirmInfo } from 'components/v2/confirm-dialog/confirmInfo';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import InputFormField from 'components/v2/form/InputFormField';
import SelectDataField from 'components/v2/form/select-data-field/SelectDataField';
import { NOT_ASSIGNED } from 'constants/constants';
import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { useCpcrsOfMainPcr, usePcrsOfProductCategory, useProductCategories } from 'services/api/standardEpdQueries';
import CopyEpdService from 'services/CopyEpdService';
import {
  CopyEpdFormikModel,
  CopyEpdV2Model,
  DataSourceModel,
  EPDVerifierModel,
  EpdCopyGroupWizardModel,
  EpdMachineReadableEnum,
  EpdProductInformationEnum,
  MembershipModel,
  PCRModel,
  VerificationType,
} from 'services/EpdClient';
import styled from 'styled-components';
import { StyledForm } from 'styles/Styles.styled';
import { CheckBoxPanel, FieldPanelFullWidthInput, FieldPanelInput } from 'styles/v2/Styles.styled';
import { Option } from 'types/types';
import { applyPcrChangeToStandards } from 'util/conformityStandards';
import { conformityStandardsInfoPlainText } from 'components/v2/conformity-standards/ConformityStandardsInfo';
import filterToolsForEpdVerificationIntoOptions from 'util/filterToolsForEpdVerificationIntoOptions';
import { isPcrExpired } from 'util/utils';
import * as Yup from 'yup';
import DownloadPcrDocument from '../DownloadPcrDocument';
import useConformityStandards from './useConformityStandards';

const WizardStep1: React.FunctionComponent<{
  groupData?: EpdCopyGroupWizardModel;
  copyGroupValues?: CopyEpdFormikModel;
  activeStep: number;
  companyName?: string;
  previousStep: number;
  setCopyGroupValues: any;
  handleSubmit: any;
  selectedProductCategoryId?: string;
  setSelectedProductCategoryId: (id?: string) => void;
  selectedPcr?: PCRModel;
  setSelectedPcr: (p?: PCRModel) => void;
  selectedCpcr?: PCRModel;
  setSelectedCpcr: (p?: PCRModel) => void;
}> = ({
  groupData,
  copyGroupValues,
  activeStep,
  companyName,
  previousStep,
  setCopyGroupValues,
  handleSubmit,
  selectedProductCategoryId,
  setSelectedProductCategoryId,
  selectedPcr,
  setSelectedPcr,
  selectedCpcr,
  setSelectedCpcr,
}) => {
  const { t } = useTranslation();
  const initialValues = {
    sourceEpdVersionId: groupData?.sourceEpdVersionId,
    edpCopyGroupName: groupData?.name ?? '',
    collectionId: groupData?.collectionId,
    practitionersIds: groupData?.practitionersIds ?? [],
    verifierId: groupData?.verifierId,
    singleCopy: false,
    copyAllDataset: groupData?.copyAllData,
    count: groupData?.count,
    verificationType:
      groupData?.epdCopyGroupDataSources?.verificationTypeDataSource?.length === 1
        ? groupData.epdCopyGroupDataSources.verificationTypeDataSource[0]
        : groupData?.verificationType,
    preverifiedTool: groupData?.preverifiedTool,
    preverifiedToolVersion: groupData?.preverifiedToolVersion,
    keywordsList: groupData?.selectedKeywordsList ?? [],
    productInformationSectionsList: groupData?.selectedProductInformationSectionsList ?? [],
    machineReadableSectionsList: groupData?.selectedMachineReadableSectionsList ?? [],
    isSectorEPD: groupData?.isSectorEPD ?? false,
    statementOfConformityWithStandards: Array.isArray(groupData?.statementOfConformityWithStandards)
      ? groupData.statementOfConformityWithStandards
      : groupData?.statementOfConformityWithStandards?.toString().split(',') ?? [],
    productCategoryId: groupData?.productCategoryId,
    pcr: groupData?.pcr,
    cpcr: groupData?.cpcr,
  };

  const getDevelopersDataSourceOptions = (values: MembershipModel[] | undefined) => {
    const grouped = Object.entries(_.groupBy(values, (v) => v.userId));
    const result = grouped.map(([userId, value]) => {
      const roles = value.map((p) => p.roleName);
      return {
        value: userId,
        label: `${values?.find((membership: MembershipModel) => membership.userId === userId)?.userName} (${roles
          .sort()
          .join(', ')})`,
      } as Option;
    });

    return result;
  };

  const productCategories = useProductCategories().data;
  const pcrs = usePcrsOfProductCategory(selectedProductCategoryId).data;
  const cpcrs = useCpcrsOfMainPcr(selectedPcr?.id).data;

  const [nowEpoch] = useState<number>(Date.now());
  const conformityStandardsOptions = useConformityStandards(selectedPcr);

  const productCategoriesOptions: Option[] = (productCategories ?? []).map((x) => ({ value: x.id!, label: x.title! }));

  const pcrOptions: Option[] = (pcrs ?? []).map((x) => ({
    value: x.id!,
    label: x.fullName ?? NOT_ASSIGNED,
    disabled: isPcrExpired(x.validUntil, nowEpoch),
  }));

  const cpcrOptions: Option[] = (cpcrs ?? []).map((x) => ({
    value: x.id!,
    label: x.fullName ?? NOT_ASSIGNED,
    disabled: isPcrExpired(x.validUntil, nowEpoch),
  }));

  const isPcrDisabled = groupData?.id !== undefined || !selectedProductCategoryId || pcrOptions.length === 0;

  const isCpcrDisabled = groupData?.id !== undefined || !selectedPcr || cpcrOptions.length === 0;

  const [developersDataSource, setDevelopersDataSource] = useState<Option[]>(
    getDevelopersDataSourceOptions(groupData?.developersSource)
  );

  useEffect(() => {
    setDevelopersDataSource(getDevelopersDataSourceOptions(groupData?.developersSource));
  }, [groupData?.developersSource]);

  const productInformationSectionDataSource =
    groupData?.epdCopyGroupDataSources?.productInformationSectionDataSource.map((value: EpdProductInformationEnum) => {
      return {
        value: value,
        label: t(`epdSectorsEnum.${value}`),
      } as Option;
    }) ?? [];

  const getMachineReadableSectionDataSource = () => {
    const options = [
      EpdMachineReadableEnum.ReferenceFlows,
      EpdMachineReadableEnum.ConversionFactorToMass,
      EpdMachineReadableEnum.MassOfDeclaredUnit,
      EpdMachineReadableEnum.MaterialProperties,
      EpdMachineReadableEnum.GeographicalScopeDescription,
      EpdMachineReadableEnum.TechnologyDescription,
      EpdMachineReadableEnum.TechnicalPurposeOfProduct,
      EpdMachineReadableEnum.Subtype,
      EpdMachineReadableEnum.DataSources,
      EpdMachineReadableEnum.ReferencePackageVersion15804,
      EpdMachineReadableEnum.CharacterisationMethods,
      EpdMachineReadableEnum.UseAdvice,
      EpdMachineReadableEnum.EpdBasedOnUnit,
      EpdMachineReadableEnum.ProductToDeclare,
      EpdMachineReadableEnum.BiogenicCarbonContent,
      EpdMachineReadableEnum.AdditionalInformationAboutTheUnderlyingLCABasedInformation,
    ];

    return options.map((value) => {
      return {
        value: value,
        label: t(`epdSectorsEnum.${value}`),
      } as Option;
    });
  };

  const handleEPDCopyGroupCreate = async (values: any) => {
    const sourceEPD = groupData?.epdCopyGroupDataSources?.epdsToCopy.find(
      (epd: DataSourceModel) => epd.id === values.sourceEpdVersionId
    );
    confirmAlert({
      caption: values.singleCopy
        ? 'You have requested a single EPD copy creation without a group'
        : `You have chosen ${sourceEPD?.name} as source EPD dataset.`,
      description: values.singleCopy ? (
        <div>
          Once you click the button "Submit" will be re-directed to the main EPD creation flow. Selected source EPD and EPD
          dataset will be saved and applied to a new EPD copy.
        </div>
      ) : (
        <div>
          Please note, once you click the button "Submit" you will not be able to make changes to Step 1 - Select source EPD.
        </div>
      ),
      acceptLabel: 'Submit',
      rejectLabel: 'Cancel',
      onConfirm: async () => {
        await handleSubmit({
          ...values,
          pcrId: selectedPcr?.id,
          cpcrId: selectedCpcr?.id,
        } as CopyEpdV2Model);
      },
      onReject: () => {},
    });
  };

  const handleChangeSourceEpdOrCopyAllDataSet = async (e: any, values: any, setValues: any, setFieldTouched?: any) => {
    if (!groupData) {
      return;
    }

    if (e.target.name === 'sourceEpdVersionId' && values.copyAllDataset === true && e.target.value === '') {
      setValues({ ...values, sourceEpdVersionId: '', copyAllDataset: false });
      return;
    }
    const sourceEPDId = e.target.name === 'copyAllDataset' ? values.sourceEpdVersionId : e.target.value;
    if (
      (e.target.name === 'copyAllDataset' && e.target.checked === true && sourceEPDId) ||
      (e.target.name === 'sourceEpdVersionId' && values.copyAllDataset === true && sourceEPDId)
    ) {
      try {
        const sourceEpdData = await CopyEpdService.getEpdDataToCopyAllDataset(sourceEPDId);
        if (!sourceEpdData) {
          throw new Error('EPD to copy is undefined.');
        }

        Object.keys(values).forEach((key) => {
          setFieldTouched(key as keyof typeof values, true);
        });
        const isCopyAllCheckbox = e.target.name === 'copyAllDataset';
        const hasUncopiedPcrData = !sourceEpdData.isCpcrCopied || !sourceEpdData.isPcrCopied;
        const expiredFieldName = !sourceEpdData.isCpcrCopied && sourceEpdData.isPcrCopied ? 'c-PCR' : 'PCR';

        if (hasUncopiedPcrData) {
          confirmInfo({
            caption: `Source EPD contains expired ${expiredFieldName}!`,
            description: (
              <div>
                <p>
                  You are about to copy all information from the source EPD, except for the {expiredFieldName} field which
                  contains an expired value.
                </p>
                <p style={{ marginBottom: 0, marginTop: '20px' }}>
                  <b>Important:</b> This field will not be copied automatically.
                  <br />
                  Please select a valid {expiredFieldName} manually before proceeding.
                </p>
              </div>
            ),
          });
        }

        setValues({
          ...values,
          sourceEpdVersionId: e.target.name === 'sourceEpdVersionId' ? sourceEPDId : values.sourceEpdVersionId,
          copyAllDataset: isCopyAllCheckbox
            ? hasUncopiedPcrData
              ? e.target.unchecked
              : e.target.checked
            : values.copyAllDataset,
          practitionersIds: sourceEpdData.practitionersIds.filter((x: any) =>
            groupData.developersSource?.map((m) => m.userId).includes(x)
          ),
          verifierId: groupData.verifiersSource?.find((x) => x.contactUserId === sourceEpdData.verifierId)
            ? sourceEpdData.verifierId
            : null,
          verificationType: groupData.epdCopyGroupDataSources?.verificationTypeDataSource?.find(
            (x) => x === sourceEpdData.verificationType
          )
            ? sourceEpdData.verificationType
            : null,
          preverifiedTool: sourceEpdData.preverifiedTool,
          preverifiedToolVersion: sourceEpdData.preverifiedToolVersion,
          keywordsList: sourceEpdData.selectedKeywordsList ?? [],
          isSectorEPD: sourceEpdData.isSectorEPD,
          productInformationSectionsList: productInformationSectionDataSource.map((option: Option) => option.value),
          machineReadableSectionsList: getMachineReadableSectionDataSource().map((option: Option) => option.value),
          statementOfConformityWithStandards: sourceEpdData.statementOfConformityWithStandards?.toString().split(',') ?? [],
          productCategoryId: sourceEpdData.productCategoryId,
          pcr: sourceEpdData.pcr,
          cpcr: sourceEpdData.cpcr,
        });

        setSelectedProductCategoryId(sourceEpdData.productCategoryId);
        setSelectedPcr(sourceEpdData.pcr);
        setSelectedCpcr(sourceEpdData.cpcr);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleChangeSingleCopy = async (e: any, values: any, setValues: any) => {
    setValues({
      ...values,
      singleCopy: e.target.name === 'singleCopy' ? e.target.checked : values.singleCopy,
      edpCopyGroupName: '',
      count: 1,
      collectionId: undefined,
    });
  };

  const handleChangeVerificationType = async (e: any, values: typeof initialValues, setFieldValue: any) => {
    setFieldValue('copyAllDataset', false);
    const oldType = values.verificationType;
    const newType = e.target.value;
    if (newType === VerificationType.EPDProcessCertification || newType === VerificationType.EPDPreverifiedTool) {
      setFieldValue('verifierId', undefined, false);
    }

    if (
      (oldType !== VerificationType.EPDPreverifiedTool && newType === VerificationType.EPDPreverifiedTool) ||
      (oldType === VerificationType.EPDPreverifiedTool && newType !== VerificationType.EPDPreverifiedTool)
    ) {
      setFieldValue('preverifiedTool', null, false);
      setFieldValue('preverifiedToolVersion', null, false);
    }
  };

  const handleClearFrom = (resetForm: any) => {
    confirmAlert({
      caption: 'Do you want to clear entered Source EPD data?',
      description: <div>All entered data will removed.</div>,
      acceptLabel: 'Clear data',
      rejectLabel: 'Cancel',
      onConfirm: () => {
        resetForm();
        setSelectedPcr(undefined);
        setSelectedCpcr(undefined);
      },
      onReject: () => {},
    });
  };

  const handleChangeProductCategory = (option: Option | null, setFieldValue: any) => {
    const newProductCategoryId = option?.value;
    setFieldValue('copyAllDataset', false);
    setFieldValue('pcr', undefined);
    setFieldValue('cpcr', undefined);
    setFieldValue('productCategoryId', newProductCategoryId);
    setSelectedProductCategoryId(newProductCategoryId);
    setSelectedPcr(undefined);
    setSelectedCpcr(undefined);
  };

  const handleChangePcr = (e: SingleValue<Option>, values: any, setFieldValue: any) => {
    if (!pcrs) return;

    setFieldValue('copyAllDataset', false);

    if (!e) {
      setFieldValue('pcr', undefined);
      setFieldValue('cpcr', undefined);
      setSelectedPcr(undefined);
      setSelectedCpcr(undefined);
      return;
    }

    const newPcr = pcrs.find((p) => p.id === e.value) ?? ({ id: e.value } as PCRModel);

    const base = Array.isArray(values.statementOfConformityWithStandards) ? values.statementOfConformityWithStandards : [];

    const nextStandards = applyPcrChangeToStandards(base, !!newPcr.isConstruction);

    setFieldValue('pcr', newPcr);
    setFieldValue('cpcr', undefined);
    setFieldValue('statementOfConformityWithStandards', nextStandards);

    setSelectedPcr(newPcr);
    setSelectedCpcr(undefined);
  };

  const handleChangeCpcr = (e: SingleValue<Option>, setFieldValue: any) => {
    if (!cpcrs) return;

    setFieldValue('copyAllDataset', false);

    if (!e) {
      setFieldValue('cpcr', undefined);
      setSelectedCpcr(undefined);
      return;
    }

    const newCpcr = cpcrs.find((c) => c.id === e.value) ?? ({ id: e.value } as PCRModel);
    setFieldValue('cpcr', newCpcr);
    setSelectedCpcr(newCpcr);
  };

  const validationSchema = Yup.object().shape({
    edpCopyGroupName: Yup.string().when('singleCopy', {
      is: false,
      then: Yup.string().required(t('messages.required')),
    }),
    sourceEpdVersionId: Yup.string().required(t('messages.required')),
    collectionId: Yup.string().nullable(),
    practitionersIds: Yup.array<string>()
      .min(1, t('messages.required'))
      .test({
        name: 'not-same-verifier-company',
        message: t('epdWizard.wizardStep1.properties.epdDeveloper.messages.sameCompany'),
        test: function (value: any) {
          const selectedVerifierId = this.parent.verifierId;
          const epdVerifier = groupData?.verifiersSource?.find(
            (verifier: EPDVerifierModel) => verifier.contactUserId === selectedVerifierId
          );
          const selectedEpdDevelopers = groupData?.developersSource?.filter((developer: MembershipModel) =>
            value?.includes(developer.userId)
          );

          return (
            !selectedEpdDevelopers ||
            !epdVerifier ||
            selectedEpdDevelopers.length === 0 ||
            !selectedEpdDevelopers.find(
              (developer: MembershipModel) =>
                developer.companyId === epdVerifier.companyId &&
                developer.userId !== epdVerifier.contactUserId &&
                !epdVerifier.isExternalMembership
            )
          );
        },
      })
      .test({
        name: 'not-same-verifier-user',
        message: t('epdWizard.wizardStep1.properties.epdDeveloper.messages.sameUser'),
        test: function (value: any) {
          const selectedVerifierId = this.parent.verifierId;
          const epdVerifier = groupData?.verifiersSource?.find((verifier) => verifier.contactUserId === selectedVerifierId);
          const selectedEpdDevelopers = groupData?.developersSource?.filter((developer) =>
            value?.includes(developer.userId)
          );

          return (
            !selectedEpdDevelopers ||
            !epdVerifier ||
            selectedEpdDevelopers.length === 0 ||
            !selectedEpdDevelopers.find((developer) => developer.userId === epdVerifier.contactUserId)
          );
        },
      }),
    verifierId: Yup.string()
      .nullable()
      .when('verificationType', {
        is: (verificationType) => verificationType === VerificationType.EPDVerification,
        then: Yup.string().required(t('messages.required')),
      })
      .test({
        name: 'not-same-developer-company',
        message: t('epdWizard.wizardStep1.properties.epdVerifier.messages.sameCompany'),
        test: function (value: any) {
          const selectedPractitionersIds = this.parent.practitionersIds as string[];
          const epdVerifier = groupData?.verifiersSource?.find(
            (verifier: EPDVerifierModel) => verifier.contactUserId === value
          );
          const selectedEpdDevelopers = groupData?.developersSource?.filter((developer: MembershipModel) =>
            selectedPractitionersIds?.includes(developer.userId)
          );

          return (
            !epdVerifier ||
            !selectedEpdDevelopers ||
            selectedEpdDevelopers.length === 0 ||
            !selectedEpdDevelopers.find(
              (developer: MembershipModel) =>
                developer.companyId === epdVerifier.companyId &&
                developer.userId !== epdVerifier.contactUserId &&
                !epdVerifier.isExternalMembership
            )
          );
        },
      })
      .test({
        name: 'not-same-developer',
        message: t('epdWizard.wizardStep1.properties.epdVerifier.messages.sameUser'),
        test: function (value: any) {
          const selectedPractitionersIds = this.parent.practitionersIds as string[];
          const epdVerifier = groupData?.verifiersSource?.find((verifier) => verifier.contactUserId === value);
          const selectedEpdDevelopers = groupData?.developersSource?.filter((developer) =>
            selectedPractitionersIds?.includes(developer.userId)
          );

          return (
            !epdVerifier ||
            !selectedEpdDevelopers ||
            selectedEpdDevelopers.length === 0 ||
            !selectedEpdDevelopers.find((developer) => developer.userId === epdVerifier.contactUserId)
          );
        },
      }),
    pcr: Yup.object<PCRModel>().nullable(),
    count: Yup.number().required(t('messages.required')),
    verificationType: Yup.mixed<VerificationType | null>().required(t('messages.required')),
    preverifiedTool: Yup.mixed<Option | null>().when('verificationType', {
      is: VerificationType.EPDPreverifiedTool,
      then: (s: Yup.StringSchema) => s.required(t('messages.required')),
    }),
    preverifiedToolVersion: Yup.mixed<Option | null>().when('verificationType', {
      is: VerificationType.EPDPreverifiedTool,
      then: (s: Yup.StringSchema) => s.required(t('messages.required')),
    }),
    keywordsList: Yup.array<string>(),
    productInformationSectionsList: Yup.array<EpdProductInformationEnum>(),
    machineReadableSectionsList: Yup.array<EpdMachineReadableEnum>(),
    isSectorEPD: Yup.boolean().nullable(),
    statementOfConformityWithStandards: Yup.array<string>().min(1, t('messages.required')),
  });

  const FormikPersist = ({ formikValues, setValues, setCopyGroupValues }: any) => {
    useEffect(() => {
      setCopyGroupValues(formikValues);
    }, [formikValues]);

    useEffect(() => {
      if (copyGroupValues && activeStep === 1 && previousStep !== activeStep) {
        setValues(copyGroupValues);
      }
    }, [activeStep]);

    return null;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleEPDCopyGroupCreate}
      enableReinitialize
    >
      {({
        values,
        setValues,
        setFieldValue,
        setFieldTouched,
        resetForm,
        isSubmitting,
        dirty,
        submitCount,
        isValid,
        errors,
      }) => {
        const toolOptions = filterToolsForEpdVerificationIntoOptions(
          groupData?.epdCopyGroupDataSources?.preverifiedToolDataSource ?? [],
          groupData?.epdCopyGroupDataSources?.preverifiedToolVersionDataSource ?? [],
          values.verificationType ?? VerificationType.EPDVerification,
          companyName ?? ''
        );
        const toolVersionOptions = toolOptions.find((o) => o.value === values.preverifiedTool?.value)?.versions ?? [];

        return (
          <StyledForm>
            {groupData?.id === undefined && (
              <FormikPersist formikValues={values} setValues={setValues} setCopyGroupValues={setCopyGroupValues} />
            )}

            <Prompt when={dirty && submitCount === 0} message="Are you sure you want to leave? You have unsaved changes." />
            <FieldPanel>
              <FieldPanelHeader>EPD copy initiation</FieldPanelHeader>

              <FieldPanelInput>
                <InputFormField
                  label={'Group copy name'}
                  placeholder={values.singleCopy ? '' : 'Type group name'}
                  name="edpCopyGroupName"
                  maxLength={90}
                  tooltip={
                    'All the copies you create will be placed in a group, you decide the name of the group in this field. This function is developed so that you can send all EPDs at the same time for verification, without having to click one by one. After publication, the group will cease and you will see all EPDs as single EPDs on your dashboard.'
                  }
                  tooltipPosition="right"
                  required={!values.singleCopy}
                  disabled={groupData?.id !== undefined || values.singleCopy}
                />
                {groupData?.id === undefined && (
                  <AdditionalCheckBoxGroup>
                    <PrimeCheckbox
                      name="singleCopy"
                      label={'I need a single EPD copy without a group'}
                      handlePostChangeAction={(event: any) => {
                        handleChangeSingleCopy(event, values, setValues);
                      }}
                    />
                  </AdditionalCheckBoxGroup>
                )}
              </FieldPanelInput>

              <SelectDataField
                label="Number of copies"
                tooltip="You can select up to 50 copies"
                tooltipPosition="right"
                name="count"
                options={Array.from({ length: 50 }, (_, i) => i + 1).map((i) => {
                  return { value: i, label: i } as unknown as Option;
                })}
                required
                disabled={groupData?.id !== undefined || values.singleCopy === true}
              />

              <FieldPanelInput>
                <SelectDataField
                  label="Source EPD"
                  tooltip="Select a source EPD to copy datasets for new EPD copies. Only published EPDs can be selected."
                  tooltipPosition="right"
                  name="sourceEpdVersionId"
                  options={
                    groupData?.epdCopyGroupDataSources?.epdsToCopy.map((epds: DataSourceModel) => {
                      return {
                        value: epds.id,
                        label: epds.name,
                      } as Option;
                    }) ?? []
                  }
                  required
                  isClearable
                  disabled={groupData?.id !== undefined}
                  handlePostChangeAction={(event: any) => {
                    handleChangeSourceEpdOrCopyAllDataSet(event, values, setValues, setFieldTouched);
                  }}
                />
                {groupData?.id === undefined && (
                  <AdditionalCheckBoxGroup>
                    <PrimeCheckbox
                      name="copyAllDataset"
                      label="Copy all information from this EPD"
                      handlePostChangeAction={(event: any) => {
                        handleChangeSourceEpdOrCopyAllDataSet(event, values, setValues, setFieldTouched);
                      }}
                    />
                  </AdditionalCheckBoxGroup>
                )}
              </FieldPanelInput>
              <SelectDataField
                label="Add EPD copies to collection"
                tooltip={'You can add EPD copies to collection to keep the EPDs together after publication.'}
                tooltipPosition="right"
                name="collectionId"
                options={
                  groupData?.epdCopyGroupDataSources?.collectionDataSource.map((collection: DataSourceModel) => {
                    return {
                      value: collection.id,
                      label: collection.name,
                    } as Option;
                  }) ?? []
                }
                isClearable
                isDisabled={
                  groupData?.id !== undefined ||
                  groupData?.epdCopyGroupDataSources?.collectionDataSource?.length === 0 ||
                  values.singleCopy
                }
              />
            </FieldPanel>
            <FieldPanel>
              <FieldPanelHeader>EPD verification</FieldPanelHeader>

              <SelectDataField
                label={t('epdWizard.wizardStep1.properties.verificationType.title')}
                name="verificationType"
                tooltip={'Select verification type'}
                tooltipPosition="right"
                options={
                  groupData?.epdCopyGroupDataSources?.verificationTypeDataSource.map((value: VerificationType) => {
                    return {
                      value: value,
                      label: t(`verificationTypeEnum.${value}`),
                    } as Option;
                  }) ?? []
                }
                isClearable
                required
                disabled={groupData?.id !== undefined}
                handlePostChangeAction={(event: any) => {
                  handleChangeVerificationType(event, values, setFieldValue);
                }}
              />
              <SelectDataField
                label="EPD Verifier"
                tooltip="If you don't find the right verifier in the list, invite EPD Verifiers in your organisation's settings."
                tooltipPosition="right"
                name="verifierId"
                options={
                  groupData?.verifiersSource?.map((user) => {
                    return {
                      value: user.contactUserId,
                      label: user.displayName,
                    } as Option;
                  }) ?? []
                }
                isClearable
                required={values.verificationType === VerificationType.EPDVerification}
                disabled={
                  !!groupData?.id ||
                  (values.verificationType && values.verificationType !== VerificationType.EPDVerification)
                }
                handlePostChangeAction={() => {
                  setFieldValue('copyAllDataset', false);
                  setFieldTouched('practitionersIds', true);
                }}
              />

              <CompilerSelectControlledComponent
                label={t('epdWizard.wizardStep1.properties.preVerifiedTool.title')}
                name="preverifiedTool"
                tooltip={
                  'A pre-verified EPD tool contains data and calculation models to simplify the LCA calculation procedure based on a reference PCR. It is pre-verified to ensure that it produces correct data, given the correct input.'
                }
                placeholder="Select"
                tooltipPosition="right"
                options={toolOptions}
                value={values.preverifiedTool}
                onChanged={(name, newValue) => {
                  if (newValue === values.preverifiedTool?.value) {
                    return;
                  }
                  const newOption = toolOptions.find((o) => o.value === newValue);
                  setValues({
                    ...values,
                    [name]: newOption,
                    preverifiedToolVersion: null,
                    copyAllDataset: false,
                  });
                }}
                isClearable
                disabled={groupData?.id !== undefined}
                error={errors.preverifiedTool}
              />

              <CompilerSelectControlledComponent
                label={t('epdWizard.wizardStep1.properties.preVerifiedToolVersion.title')}
                name="preverifiedToolVersion"
                tooltip="Select pre-verified EPD tool version"
                placeholder="Select"
                tooltipPosition="right"
                options={toolVersionOptions}
                value={values.preverifiedToolVersion}
                onChanged={(name, newValue) => {
                  const newOption = toolVersionOptions.find((o) => o.value === newValue);
                  setValues({
                    ...values,
                    [name]: newOption,
                    copyAllDataset: false,
                  });
                }}
                isClearable
                disabled={groupData?.id !== undefined || !values.preverifiedTool}
                error={errors.preverifiedToolVersion}
              />
            </FieldPanel>

            <FieldPanel>
              <FieldPanelHeader>EPD dataset</FieldPanelHeader>

              <SelectDataField
                label="LCA Practitioner"
                tooltip={
                  "If you don't find the right verifier in the list, invite LCA Practitioners in your organisation's settings."
                }
                tooltipPosition="right"
                name="practitionersIds"
                options={developersDataSource}
                isClearable
                required
                isMulti
                disabled={groupData?.id !== undefined}
                handlePostChangeAction={() => {
                  setFieldValue('copyAllDataset', false);
                  setFieldTouched('verifierId', true);
                }}
              />

              <SelectDataField
                label="Product information"
                name="productInformationSectionsList"
                tooltip={
                  'In this drop-down menu, you can select one or more product information data, which is then copied from the source EPD.'
                }
                tooltipPosition="right"
                options={productInformationSectionDataSource}
                isMulti
                isClearable
                disabled={groupData?.id !== undefined}
                handlePostChangeAction={() => {
                  setFieldValue('copyAllDataset', false);
                }}
              />

              <FieldPanelInput>
                <SelectDataField
                  label="Standards conformance"
                  tooltip={conformityStandardsInfoPlainText}
                  tooltipPosition="title"
                  name="statementOfConformityWithStandards"
                  options={conformityStandardsOptions}
                  isClearable
                  required
                  isMulti
                  disabled={groupData?.id !== undefined}
                  handlePostChangeAction={() => {
                    setFieldValue('copyAllDataset', false);
                  }}
                />
              </FieldPanelInput>

              <SelectDataField
                label="Information for machine-readable EPDs"
                name="machineReadableSectionsList"
                tooltip={
                  'In this drop-down menu, you can select one or more product information data, which is then copied from the source EPD.'
                }
                tooltipPosition="right"
                options={getMachineReadableSectionDataSource()}
                isMulti
                isClearable
                disabled={groupData?.id !== undefined}
                handlePostChangeAction={() => {
                  setFieldValue('copyAllDataset', false);
                }}
              />

              <FieldPanelFullWidthInput>
                <SelectDataField
                  label="Product category"
                  tooltip={'ISO 14025 and EN 15804 emphasize defining product categories clearly in EPDs.'}
                  tooltipPosition="right"
                  name="productCategoryId"
                  options={productCategoriesOptions}
                  onChange={(option: Option | null) => handleChangeProductCategory(option, setFieldValue)}
                  isClearable
                  required
                  disabled={groupData?.id !== undefined}
                />
              </FieldPanelFullWidthInput>

              <FieldPanelFullWidthInput>
                <SelectDataField
                  label="PCR"
                  tooltip="Product category rules."
                  tooltipPosition="right"
                  placeholder={'Select PCR'}
                  name="pcr"
                  options={pcrOptions}
                  value={pcrOptions?.find((o) => o.value === selectedPcr?.id) ?? null}
                  onChange={(e: SingleValue<Option>) => handleChangePcr(e, values, setFieldValue)}
                  isClearable
                  required={!!values.productCategoryId}
                  disabled={groupData?.id !== undefined || isPcrDisabled}
                  isOptionDisabled={(option: any) => option.disabled}
                  handlePostChangeAction={() => {
                    setFieldValue('copyAllDataset', false);
                  }}
                />
                {!!selectedPcr && <DownloadPcrDocument pcr={selectedPcr} pcrType="main" label="Download PCR" />}
              </FieldPanelFullWidthInput>

              <FieldPanelFullWidthInput>
                <SelectDataField
                  label="C-PCR"
                  tooltip="Complementary product category rules."
                  tooltipPosition="right"
                  placeholder={'Select C-PCR'}
                  name="cpcr"
                  options={cpcrOptions}
                  value={cpcrOptions?.find((o) => o.value === selectedCpcr?.id) ?? null}
                  onChange={(e: SingleValue<Option>) => handleChangeCpcr(e, setFieldValue)}
                  isClearable
                  required={false}
                  disabled={groupData?.id !== undefined || isCpcrDisabled}
                  isOptionDisabled={(option: any) => option.disabled}
                  handlePostChangeAction={() => {
                    setFieldValue('copyAllDataset', false);
                  }}
                />
                {!!selectedCpcr && <DownloadPcrDocument pcr={selectedCpcr} pcrType="cpcr" label="Download c-PCR" />}
              </FieldPanelFullWidthInput>

              <FieldPanelFullWidthInput>
                <SectorEPDCheckBoxPanel disabled={groupData?.id !== undefined}>
                  <PrimeCheckbox
                    name="isSectorEPD"
                    label={t('epdWizard.wizardStep1.properties.sectorEpd')}
                    tooltip={t('epdWizard.wizardStep1.tooltips.sectorEpd')}
                    tooltipPosition="right"
                    disabled={groupData?.id !== undefined}
                    handlePostChangeAction={() => {
                      setFieldValue('copyAllDataset', false);
                    }}
                  />
                </SectorEPDCheckBoxPanel>
              </FieldPanelFullWidthInput>
            </FieldPanel>
            <ButtonPanel visible={groupData?.id === undefined}>
              <SecondaryButton
                label="Clear data"
                disabled={groupData?.id !== undefined || (!dirty && !selectedPcr)}
                onClick={() => {
                  handleClearFrom(resetForm);
                }}
                outlined
                type="button"
              />
              <PrimaryButton
                style={{ marginLeft: '16px' }}
                label="Submit data"
                disabled={isSubmitting || !dirty || !isValid || !selectedPcr || groupData?.id !== undefined}
                type="submit"
              />
            </ButtonPanel>
          </StyledForm>
        );
      }}
    </Formik>
  );
};

export const FixedRow = styled.div`
  grid-column-gap: 10px;
  display: grid;
  grid-template-columns: 100%;
`;

const FormPanel = styled.div`
  justify-content: space-between;
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 16px;
  padding: 30px;
  border-radius: 4px;
  background: ${(props) => props.theme.colors.primaryWhite};

  /* Shadow/Block */
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
`;

const ButtonPanel = styled(FormPanel)<{ visible?: string | boolean }>`
  justify-content: flex-end;
  padding: 16px;
  display: ${(props) => (props.visible ? 'flex' : 'none')};
`;

const FieldPanel = styled(FormPanel)`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 22px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

const FieldPanelHeader = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
  ${(props) => props.theme.typography.headerCaptionSemiBold}
`;

const SectorEPDCheckBoxPanel = styled(CheckBoxPanel)<{ disabled?: string | boolean }>`
  margin-top: 22px;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryOrange01};
  .p-checkbox,
  .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
  .p-checkbox .p-checkbox-box .p-checkbox-icon {
    font-size: 10px;
    font-weight: 700;
  }
  .p-checkbox-box.p-highlight.p-disabled {
    background-color: ${(props) => props.theme.colors.elementsButtonDisabled};
    border-color: ${(props) => props.theme.colors.elementsButtonDisabled};
  }
`;

const AdditionalCheckBoxGroup = styled.div`
  margin-top: 0.25rem;
  max-height: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .p-checkbox,
  .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
  .p-checkbox .p-checkbox-box .p-checkbox-icon {
    font-size: 10px;
    font-weight: 700;
  }
  .p-checkbox-box.p-highlight.p-disabled {
    background-color: #757575;
    border-color: #757575;
  }
  .p-checkbox .p-checkbox-box {
    border: 1px solid ${(props) => props.theme.colors.elementsButtonDisabled};
    border-radius: 2px;
  }
`;

export default WizardStep1;
